/**
 * @module features/client-device-management/api/configApi
 * @description API fetchers and TanStack Query hooks for dynamic parameter catalogs
 * and device-type-specific client configurations across 10+ IoT gateway categories.
 */
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiSuccessEnvelope, AppError } from "@/lib/api-client";
import {
  CatalogDefinition,
  CatalogDefinitionSchema,
  DeviceConfigParameter,
  DeviceConfigParameterSchema,
} from "../types";
import { clientDeviceKeys, parsePayloadOrThrow } from "./assignmentApi";

const BASE = "/super-admin/client-devices";

/**
 * Retrieves all configuration parameters for an assigned device merged with
 * its device-type catalog metadata (labels, data types, validation rules).
 */
export async function getAssignmentConfigs(
  assignmentId: string
): Promise<DeviceConfigParameter[]> {
  try {
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/configs/`
    );
    return parsePayloadOrThrow(
      z.array(DeviceConfigParameterSchema),
      res.data?.data ?? [],
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to load device configuration parameters.");
  }
}

/**
 * Updates multiple configuration parameters for an assigned device atomically.
 */
export async function updateAssignmentConfigs(
  assignmentId: string,
  configs: Record<string, string>
): Promise<void> {
  try {
    await apiClient.put<ApiSuccessEnvelope<unknown>>(
      `${BASE}/${assignmentId}/configs/update/`,
      { configs }
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to update device configurations.");
  }
}

/**
 * Fetches declarative parameter catalog definitions, optionally filtered by device_type.
 */
export async function getConfigCatalogs(
  deviceType?: string
): Promise<CatalogDefinition[]> {
  try {
    const qp = new URLSearchParams();
    if (deviceType && deviceType !== "all") qp.set("device_type", deviceType);
    const suffix = qp.toString() ? `?${qp.toString()}` : "";
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(
      `${BASE}/config-catalogs/${suffix}`
    );
    return parsePayloadOrThrow(
      z.array(CatalogDefinitionSchema),
      res.data?.data ?? [],
      res.data?.request_id
    );
  } catch (err: unknown) {
    throw AppError.fromUnknown(err, "Failed to load device configuration catalog.");
  }
}

// ============================================================================
// TanStack Query Hooks
// ============================================================================

export function useAssignmentConfigs(
  assignmentId: string | null | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: clientDeviceKeys.configs(assignmentId ?? ""),
    queryFn: () => getAssignmentConfigs(assignmentId!),
    enabled: Boolean(assignmentId) && enabled,
  });
}

export function useConfigCatalogs(deviceType?: string) {
  return useQuery({
    queryKey: clientDeviceKeys.catalogs(deviceType),
    queryFn: () => getConfigCatalogs(deviceType),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAssignmentConfigs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      assignmentId,
      configs,
    }: {
      assignmentId: string;
      configs: Record<string, string>;
    }) => updateAssignmentConfigs(assignmentId, configs),
    onSuccess: (_, { assignmentId }) => {
      void queryClient.invalidateQueries({
        queryKey: clientDeviceKeys.configs(assignmentId),
      });
      void queryClient.invalidateQueries({ queryKey: clientDeviceKeys.all });
    },
  });
}

